import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Tippy from "@tippyjs/react";
import { HiDocument, HiEmojiHappy, HiMicrophone, HiPaperAirplane, HiPaperClip, HiPencil, HiReply, HiUserGroup, HiX } from "react-icons/hi";
import VoiceRecorder, { voiceRecordingSupported } from "./VoiceRecorder";
import { useChat } from "./ChatProvider";
import { isTouchDevice } from "./useMedia";
import { uploadChatAttachment } from "../../controllers/chatController";
import ChatAvatar from "./ChatAvatar";
import { MENTION_TOKEN, formatFileSize, isImage, mentionsToText, textToMentions, userName } from "./chatUtils";

const MAX_FILES = 10;
const MAX_FILE_SIZE = 25 * 1024 * 1024;
const TYPING_THROTTLE_MS = 3000;
const TYPING_IDLE_MS = 4000;
const EMOJIS = "😀 😃 😄 😁 😆 😅 😂 🙂 😉 😊 😍 😘 😎 🤔 😐 😴 😮 😢 😭 😡 👍 👎 👏 🙌 🙏 💪 👀 🎉 🔥 ✅ ❌ ⭐ 💯 ❤️ 💡 📌 📎 📅 ⏰ 🚀".split(" ");

let localIdCounter = 0;

function UploadChip({ upload, onRemove }) {
  const preview = upload.previewUrl;
  return (
    <div className="relative flex items-center gap-2 pl-2 pr-7 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 max-w-[200px]">
      {preview ? (
        <img src={preview} alt="" className="w-8 h-8 rounded object-cover" />
      ) : (
        <HiDocument className="w-6 h-6 text-gray-500 shrink-0" />
      )}
      <span className="min-w-0">
        <span className="block text-xs font-medium truncate">{upload.file.name}</span>
        <span className={`block text-[10px] ${upload.error ? "text-red-600" : "text-gray-500"}`}>
          {upload.error || (upload.attachment ? formatFileSize(upload.file.size) : `Uploading ${upload.progress}%`)}
        </span>
      </span>
      {!upload.attachment && !upload.error && (
        <span className="absolute left-0 bottom-0 h-0.5 bg-blue-500 rounded" style={{ width: `${upload.progress}%` }} />
      )}
      <button type="button" onClick={onRemove} className="absolute right-1 top-1 p-0.5 rounded text-gray-400 hover:text-gray-700" aria-label={`Remove ${upload.file.name}`}>
        <HiX className="w-4 h-4" />
      </button>
    </div>
  );
}

// `disabled`: chat isn't ready at all. `offline`: connection down; text still works (it's queued),
// but attachments, voice notes and edits need the connection.
function Composer({ conversationId, replyTo, onCancelReply, editing, onCancelEdit, onEditLast, droppedFiles, disabled, offline = false }) {
  const { users, me, conversations, sendMessage, editMessage, sendTyping } = useChat();
  const conversation = conversations[conversationId];
  const isGroup = conversation?.type === "group";
  const [text, setText] = useState("");
  // People picked from the @ list while writing: [{ id, name }]. "@Name" becomes <@id> when sending.
  const [mentions, setMentions] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null); // { start, query } while typing "@…"
  const [mentionIndex, setMentionIndex] = useState(0);
  const [recording, setRecording] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [error, setError] = useState("");
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingState = useRef({ active: false, lastSent: 0, idleTimer: null });

  // Grow the textarea with its content, up to ~6 lines.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, [text]);

  // Put the cursor in the message box when a conversation opens.
  useEffect(() => {
    textareaRef.current?.focus();
  }, [conversationId]);

  useEffect(() => {
    if (editing) {
      setText(mentionsToText(editing.body, users));
      setMentions(
        [...(editing.body || "").matchAll(MENTION_TOKEN)].map(([, id]) => ({ id: Number(id), name: userName(users[Number(id)]) }))
      );
      textareaRef.current?.focus();
    }
    // Only when a new edit starts: re-running on `users` (presence updates) would wipe the edit in progress.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  useEffect(() => {
    if (replyTo) textareaRef.current?.focus();
  }, [replyTo]);

  const stopTyping = () => {
    const state = typingState.current;
    clearTimeout(state.idleTimer);
    if (state.active) sendTyping(conversationId, false);
    state.active = false;
  };

  useEffect(() => () => stopTyping(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const noteTyping = () => {
    const state = typingState.current;
    const now = Date.now();
    if (!state.active || now - state.lastSent > TYPING_THROTTLE_MS) {
      sendTyping(conversationId, true);
      state.active = true;
      state.lastSent = now;
    }
    clearTimeout(state.idleTimer);
    state.idleTimer = setTimeout(stopTyping, TYPING_IDLE_MS);
  };

  const uploadsRef = useRef(uploads);
  uploadsRef.current = uploads;

  const updateUpload = (localId, changes) =>
    setUploads((list) => list.map((u) => (u.localId === localId ? { ...u, ...changes } : u)));

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const room = MAX_FILES - uploadsRef.current.length;
    setError(files.length > room ? `You can attach up to ${MAX_FILES} files per message.` : "");
    const accepted = files.slice(0, Math.max(room, 0)).map((file) => {
      localIdCounter += 1;
      return {
        localId: localIdCounter,
        file,
        progress: 0,
        attachment: null,
        error: file.size > MAX_FILE_SIZE ? "Larger than 25 MB" : null,
        previewUrl: isImage(file.type) ? URL.createObjectURL(file) : null,
      };
    });
    setUploads((current) => [...current, ...accepted]);
    accepted
      .filter((u) => !u.error)
      .forEach((upload) => {
        uploadChatAttachment(upload.file, (progress) => updateUpload(upload.localId, { progress }))
          .then((attachment) => updateUpload(upload.localId, { attachment, progress: 100 }))
          .catch((err) => updateUpload(upload.localId, { error: err.response?.data?.message || "Upload failed" }));
      });
  };

  useEffect(() => {
    if (droppedFiles?.files?.length) addFiles(droppedFiles.files);
    // Text shared from another app (Android share sheet) lands in the box, ready to send or edit.
    if (droppedFiles?.text) {
      setText((current) => (current ? `${current}\n${droppedFiles.text}` : droppedFiles.text));
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [droppedFiles]);

  const removeUpload = (localId) =>
    setUploads((list) => {
      const target = list.find((u) => u.localId === localId);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return list.filter((u) => u.localId !== localId);
    });

  const uploading = uploads.some((u) => !u.attachment && !u.error);
  const readyAttachments = uploads.filter((u) => u.attachment).map((u) => u.attachment);
  const canSend = !disabled && !uploading && !(offline && editing) && (text.trim() || (!editing && readyAttachments.length));

  // ---------- @mentions (groups only) ----------
  const mentionCandidates = useMemo(() => {
    if (!mentionQuery || !isGroup) return [];
    const q = mentionQuery.query.toLowerCase();
    const people = conversation.members
      .map((m) => users[m.userId])
      .filter((u) => u && u.id !== me?.id)
      .filter((u) => !q || u.name.toLowerCase().split(/\s+/).some((part) => part.startsWith(q)) || u.email.toLowerCase().startsWith(q))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 6);
    // @everyone notifies every member of the group.
    const everyone = "everyone".startsWith(q) ? [{ id: "everyone", name: "everyone", everyone: true }] : [];
    return [...everyone, ...people];
  }, [mentionQuery, isGroup, conversation, users, me]);

  // Opens the @ list when the caret sits right after "@word" (at the start or after a space).
  const detectMention = (value, caret) => {
    if (!isGroup) return;
    const match = /(^|\s)@([\p{L}\p{N}._-]{0,30})$/u.exec(value.slice(0, caret));
    if (match) {
      setMentionQuery({ start: caret - match[2].length - 1, query: match[2] });
      setMentionIndex(0);
    } else {
      setMentionQuery(null);
    }
  };

  const insertMention = (user) => {
    const el = textareaRef.current;
    const caret = el?.selectionStart ?? text.length;
    const before = text.slice(0, mentionQuery.start);
    const inserted = `@${user.name} `;
    setText(before + inserted + text.slice(caret));
    setMentions((prev) => (prev.some((m) => m.id === user.id) ? prev : [...prev, { id: user.id, name: user.name }]));
    setMentionQuery(null);
    requestAnimationFrame(() => {
      el?.focus();
      const position = before.length + inserted.length;
      el?.setSelectionRange(position, position);
    });
  };

  const submit = async () => {
    if (!canSend) return;
    stopTyping();
    setMentionQuery(null);
    if (editing) {
      try {
        await editMessage(editing.id, textToMentions(text, mentions));
        onCancelEdit();
        setText("");
        setMentions([]);
      } catch (err) {
        setError(err.response?.data?.message || "Couldn't save your edit.");
      }
      return;
    }
    sendMessage(conversationId, { body: textToMentions(text.trim(), mentions), replyTo, attachments: readyAttachments });
    uploads.forEach((u) => u.previewUrl && URL.revokeObjectURL(u.previewUrl));
    setUploads([]);
    setText("");
    setMentions([]);
    setError("");
    onCancelReply();
  };

  const onKeyDown = (e) => {
    if (mentionCandidates.length) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const step = e.key === "ArrowDown" ? 1 : -1;
        setMentionIndex((i) => (i + step + mentionCandidates.length) % mentionCandidates.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(mentionCandidates[mentionIndex] || mentionCandidates[0]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionQuery(null);
        return;
      }
    }
    // On touch keyboards Enter adds a new line (like messaging apps); the send button sends.
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing && !isTouchDevice()) {
      e.preventDefault();
      submit();
    } else if (e.key === "Escape") {
      if (editing) {
        onCancelEdit();
        setText("");
      } else if (replyTo) onCancelReply();
    } else if (e.key === "ArrowUp" && !text && !editing && !uploads.length) {
      e.preventDefault();
      onEditLast();
    }
  };

  const onPaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length && !editing) {
      e.preventDefault();
      addFiles(files);
    }
  };

  const insertEmoji = (emoji) => {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    setText(text.slice(0, start) + emoji + text.slice(end));
    setEmojiOpen(false);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + emoji.length, start + emoji.length);
    });
  };

  const banner = editing ? (
    <div className="flex items-center gap-2 px-3 py-1.5 text-xs bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 rounded-t-lg">
      <HiPencil className="w-4 h-4" />
      <span className="flex-1">Editing message</span>
      <button type="button" onClick={() => { onCancelEdit(); setText(""); }} aria-label="Cancel edit"><HiX className="w-4 h-4" /></button>
    </div>
  ) : replyTo ? (
    <div className="flex items-center gap-2 px-3 py-1.5 text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-t-lg">
      <HiReply className="w-4 h-4 shrink-0" />
      <span className="flex-1 min-w-0 truncate">
        Replying to <strong>{replyTo.senderId === me?.id ? "yourself" : userName(users[replyTo.senderId])}</strong>
        {replyTo.body ? `: ${mentionsToText(replyTo.body, users)}` : ""}
      </span>
      <button type="button" onClick={onCancelReply} aria-label="Cancel reply"><HiX className="w-4 h-4" /></button>
    </div>
  ) : null;

  return (
    <div className="relative border-t border-gray-200 dark:border-gray-700 p-2">
      {mentionCandidates.length > 0 && (
        <ul
          role="listbox"
          aria-label="Mention someone"
          data-tour="chat-mention-list"
          className="absolute bottom-full left-2 right-2 mb-1 max-w-xs overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl z-10 py-1"
        >
          {mentionCandidates.map((user, index) => (
            <li key={user.id}>
              <button
                type="button"
                role="option"
                aria-selected={index === mentionIndex}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => insertMention(user)}
                onMouseEnter={() => setMentionIndex(index)}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm ${
                  index === mentionIndex ? "bg-blue-50 dark:bg-gray-700" : ""
                }`}
              >
                {user.everyone ? (
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">
                    <HiUserGroup className="h-4 w-4" />
                  </span>
                ) : (
                  <ChatAvatar name={user.name} src={user.avatarThumbUrl} seed={user.id} size="sm" online={!!user.online} />
                )}
                <span className="min-w-0">
                  <span className="block truncate font-medium text-gray-800 dark:text-gray-100">{user.everyone ? "@everyone" : user.name}</span>
                  {user.everyone ? (
                    <span className="block truncate text-xs text-gray-500">Notify all {conversation.members.length - 1} members</span>
                  ) : (
                    user.jobTitle && <span className="block truncate text-xs text-gray-500">{user.jobTitle}</span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {banner}
      {uploads.length > 0 && (
        <div className="flex flex-wrap gap-2 px-1 py-2">
          {uploads.map((u) => (
            <UploadChip key={u.localId} upload={u} onRemove={() => removeUpload(u.localId)} />
          ))}
        </div>
      )}
      {error && <p className="px-2 pb-1 text-xs text-red-600">{error}</p>}
      {recording ? (
        <VoiceRecorder
          conversationId={conversationId}
          replyTo={replyTo}
          onDone={(result) => {
            setRecording(false);
            if (result?.sent) onCancelReply();
            requestAnimationFrame(() => textareaRef.current?.focus());
          }}
        />
      ) : (
      <div className="flex items-end gap-1">
        {!editing && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || offline}
            className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            title="Attach files"
            aria-label="Attach files"
            data-tour="chat-attach"
          >
            <HiPaperClip className="w-5 h-5" />
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <Tippy
          theme="irg-plain"
          arrow={false}
          visible={emojiOpen}
          onClickOutside={() => setEmojiOpen(false)}
          interactive
          placement="top-start"
          appendTo={() => document.body}
          zIndex={90}
          content={
            <div className="grid grid-cols-8 gap-0.5 p-2 w-72 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700">
              {EMOJIS.map((emoji) => (
                <button key={emoji} type="button" onClick={() => insertEmoji(emoji)} className="text-xl w-8 h-8 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                  {emoji}
                </button>
              ))}
            </div>
          }
        >
          <button
            type="button"
            onClick={() => setEmojiOpen((v) => !v)}
            disabled={disabled}
            className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            title="Emoji"
            aria-label="Insert emoji"
          >
            <HiEmojiHappy className="w-5 h-5" />
          </button>
        </Tippy>
        <textarea
          ref={textareaRef}
          rows={1}
          value={text}
          disabled={disabled}
          onChange={(e) => {
            setText(e.target.value);
            detectMention(e.target.value, e.target.selectionStart);
            if (!editing && e.target.value) noteTyping();
          }}
          onSelect={(e) => mentionQuery && detectMention(e.target.value, e.target.selectionStart)}
          onBlur={() => setMentionQuery(null)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          placeholder={disabled ? "Connecting…" : offline ? "Offline – messages send when you're back" : editing ? "Edit message" : isGroup ? "Write a message — type @ to mention" : "Write a message"}
          aria-label="Message"
          data-tour="chat-composer"
          className="flex-1 resize-none max-h-[140px] px-3 py-2 rounded-2xl bg-gray-100 dark:bg-gray-800 text-base md:text-sm text-gray-800 dark:text-gray-100 border-0 focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
        {!text.trim() && !uploads.length && !editing && !disabled && !offline && voiceRecordingSupported() ? (
          <button
            type="button"
            onClick={() => {
              stopTyping();
              setRecording(true);
            }}
            className="p-2 rounded-full bg-[#2470B7] text-white hover:bg-[#1d5a93]"
            title="Record a voice message"
            aria-label="Record a voice message"
            data-tour="chat-voice"
          >
            <HiMicrophone className="w-5 h-5" />
          </button>
        ) : (
          <button
            type="button"
            // Keep the focus (and the phone keyboard) in the text box: tapping a button normally moves
            // focus to it, and this one is swapped for the mic once the box is empty, so the keyboard closed.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              const keepKeyboard = document.activeElement === textareaRef.current;
              submit();
              if (keepKeyboard) textareaRef.current?.focus();
            }}
            disabled={!canSend}
            className="p-2 rounded-full bg-[#2470B7] text-white hover:bg-[#1d5a93] disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
            title={editing ? "Save" : "Send"}
            aria-label={editing ? "Save edit" : "Send message"}
          >
            <HiPaperAirplane className="w-5 h-5 rotate-90" />
          </button>
        )}
      </div>
      )}
    </div>
  );
}

export default Composer;
